import React, { useState, useRef, useEffect } from 'react';
import { FaPaperPlane, FaTimes, FaFolder, FaCamera } from 'react-icons/fa';
import { uploadToStorage, getSignedUrl } from '../../utils/storageUtils';
import { getAuth } from 'firebase/auth';
import { useAlert } from '../../contexts/ToastContext';

const ChatInsertModal = ({ isOpen, onClose, onFileInsert }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const showAlert = useAlert();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Get current user from Firebase Auth
  const getCurrentUser = () => {
    const auth = getAuth();
    return auth.currentUser;
  };

  // Auto-open file selector when modal opens — show picker, not auto-click
  useEffect(() => {
    if (isOpen) {
      // Reset state when opening
      resetModal();
    }
  }, [isOpen]);

  // Image compression function
  const compressImage = (file, maxWidth = 1024, quality = 0.7) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;

          // Calculate new dimensions
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const getFileTypeCategory = (mimeType) => {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'document';
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    const user = getCurrentUser();
    
    if (!file || !user) {
      showAlert('Please sign in to upload files');
      return;
    }

    // Different limits for different file types
    const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB for images
    const MAX_OTHER_SIZE = 50 * 1024 * 1024; // 50MB for other files

    const maxSize = file.type.startsWith('image/') ? MAX_IMAGE_SIZE : MAX_OTHER_SIZE;
    
    if (file.size > maxSize) {
      showAlert(`File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds ${maxSize / 1024 / 1024}MB limit`);
      return;
    }

    // Compress image before setting state
    if (file.type.startsWith('image/')) {
      try {
        const compressedFile = await compressImage(file);
        setSelectedFile(compressedFile);
      } catch (error) {
        console.error('Compression failed, using original:', error);
        setSelectedFile(file);
      }
    } else {
      setSelectedFile(file);
    }
  };

  const handleFolderClick = () => {
    fileInputRef.current?.click();
  };

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };

  const simulateProgress = () => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 15;
      if (progress >= 90) {
        progress = 90;
        clearInterval(interval);
      }
      setUploadProgress(Math.min(progress, 90));
    }, 200);

    return interval;
  };

  const handleUpload = async () => {
    const user = getCurrentUser();
    if (!selectedFile || !user) {
      showAlert('Please sign in to upload files');
      return;
    }

    const progressInterval = simulateProgress();

    try {
      setIsUploading(true);

      const userFolder = `chatdownloads/${user.uid}`;
      const fileName = `${userFolder}/${Date.now()}_${selectedFile.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;

      // Upload without progress tracking (since your function doesn't support it)
      await uploadToStorage(
        fileName,
        selectedFile,
        selectedFile.type,
        'pcakeds_csv_backup1'
      );

      // Get signed URL for the uploaded file
      const signedUrl = await getSignedUrl(fileName, 'pcakeds_csv_backup1');

      clearInterval(progressInterval);
      setUploadProgress(100);

      const fileData = {
        name: selectedFile.name,
        type: getFileTypeCategory(selectedFile.type),
        size: selectedFile.size,
        preview: signedUrl, 
        downloadUrl: signedUrl, 
        bucketPath: fileName,
        uploadedAt: new Date().toISOString(),
        mimeType: selectedFile.type,
        uploadedBy: user.uid,
      };                                                          

      onFileInsert(fileData);
      
      // Short delay to show 100% progress
      setTimeout(() => {
        onClose();
        resetModal();
      }, 500);

    } catch (error) {
      console.error('Error uploading file:', error);
      clearInterval(progressInterval);
      setUploadProgress(0);
      showAlert(`Failed to upload file: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const resetModal = () => {
    setSelectedFile(null);
    setIsUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="chat-insert-modal-overlay">
      <div className="chat-insert-modal">
        <div className="modal-header">
          <button className="close-btn" onClick={handleClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-content">
          {selectedFile ? (
            <div className="file-preview">
              {selectedFile.type.startsWith('image/') && (
                <div className="full-image-preview">
                  <img 
                    src={URL.createObjectURL(selectedFile)} 
                    alt="Preview" 
                    className="full-size-image"
                  />
                </div>
              )}
              
              {!selectedFile.type.startsWith('image/') && (
                <div className="file-info">
                  <div className="file-icon">
                    📎
                  </div>
                  <div className="file-details">
                    <span className="file-name">{selectedFile.name}</span>
                    <span className="file-size">
                      {Math.round(selectedFile.size / 1024 / 1024)}MB
                    </span>
                  </div>
                </div>
              )}

              {/* File size warning */}
              {selectedFile.size > 2 * 1024 * 1024 && (
                <div className="size-warning">
                  ⚠️ Large file ({Math.round(selectedFile.size / 1024 / 1024)}MB) may take longer to upload
                </div>
              )}

              {/* Upload progress */}
              {isUploading && (
                <div className="upload-progress">
                  <div className="progress-text">
                    {uploadProgress < 100 ? `Uploading... ${Math.round(uploadProgress)}%` : 'Finalizing...'}
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="file-selector">
              <div className="upload-options">
                <button className="upload-option-btn" onClick={handleCameraClick}>
                  <FaCamera className="option-icon" />
                  <span>Camera</span>
                </button>
                <button className="upload-option-btn" onClick={handleFolderClick}>
                  <FaFolder className="option-icon" />
                  <span>Gallery</span>
                </button>
              </div>
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept="*/*"
              />
              <input
                type="file"
                ref={cameraInputRef}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept="image/*"
                capture="environment"
              />
            </div>
          )}
        </div>

        <div className="modal-actions">
          {selectedFile && (
            <button 
              className="send-btn" 
              onClick={handleUpload}
              disabled={isUploading}
            >
              <FaPaperPlane className="send-icon" />
              {isUploading ? 'Uploading...' : 'Send'}
            </button>
          )}
        </div>
      </div>

      <style jsx>{`
        .chat-insert-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .chat-insert-modal {
          background: #fff;
          border-radius: 16px;
          width: 95%;
          max-width: 460px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0,0,0,0.18);
        }

        .modal-header {
          display: flex;
          justify-content: flex-end;
          padding: 10px 15px;
          border-bottom: 1px solid #e0e0e0;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          color: #666;
          padding: 5px;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }

        .close-btn:hover {
          background: #f5f5f5;
          color: #fa4848;
        }

        .modal-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding: 0;
        }

        .upload-options {
          display: flex;
          justify-content: center;
          gap: 24px;
          padding: 40px 20px;
        }

        .upload-option-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          background: #f8f9fa;
          border: 2px solid #e0e0e0;
          border-radius: 16px;
          padding: 28px 24px;
          cursor: pointer;
          transition: all 0.2s;
          color: #555;
          min-width: 110px;
          touch-action: manipulation;
        }

        .upload-option-btn:hover,
        .upload-option-btn:active {
          border-color: #fa4848;
          color: #fa4848;
          background: #fff5f5;
        }

        .option-icon {
          font-size: 30px;
        }

        .upload-option-btn span {
          font-size: 13px;
          font-weight: 600;
        }

        .file-preview {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }

        .full-image-preview {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          padding: 12px;
          box-sizing: border-box;
        }

        .full-size-image {
          width: auto;
          height: auto;
          max-width: 100%;
          max-height: 58vh;
          object-fit: contain;
          border-radius: 8px;
        }

        .file-info {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 20px;
          background: #f8f9fa;
        }

        .file-icon {
          font-size: 24px;
        }

        .file-details {
          flex: 1;
        }

        .file-name {
          font-weight: 500;
          font-size: 15px;
          word-break: break-word;
          color: #222;
        }

        .file-size {
          font-size: 12px;
          color: #888;
          margin-top: 4px;
          display: block;
        }

        .size-warning {
          background: #fff3cd;
          border: 1px solid #ffc107;
          color: #856404;
          padding: 8px 12px;
          border-radius: 6px;
          margin: 10px;
          font-size: 12px;
          text-align: center;
        }

        .upload-progress {
          padding: 14px 16px;
          background: #f8f9fa;
        }

        .progress-text {
          font-size: 13px;
          color: #666;
          margin-bottom: 8px;
          text-align: center;
        }

        .progress-bar {
          width: 100%;
          height: 6px;
          background: #e0e0e0;
          border-radius: 3px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: #fa4848;
          transition: width 0.3s ease;
        }

        .modal-actions {
          padding: 14px 20px;
          border-top: 1px solid #e0e0e0;
          display: flex;
          justify-content: center;
          flex-shrink: 0;
        }

        .send-btn {
          background: #fa4848;
          color: white;
          border: none;
          border-radius: 25px;
          padding: 12px 32px;
          cursor: pointer;
          font-size: 15px;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background-color 0.2s;
          min-width: 120px;
          justify-content: center;
          touch-action: manipulation;
        }

        .send-btn:hover:not(:disabled) {
          background: #e03e3e;
        }

        .send-btn:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .send-icon {
          font-size: 15px;
        }

        /* ── Dark mode ── */
        html[data-theme="dark"] .chat-insert-modal {
          background: #1e1e1e;
          box-shadow: 0 8px 32px rgba(0,0,0,0.6);
        }

        html[data-theme="dark"] .modal-header {
          border-bottom-color: #333;
        }

        html[data-theme="dark"] .close-btn {
          color: #aaa;
        }

        html[data-theme="dark"] .close-btn:hover {
          background: #2a2a2a;
          color: #fa4848;
        }

        html[data-theme="dark"] .upload-option-btn {
          background: #2a2a2a;
          border-color: #3a3a3a;
          color: #bbb;
        }

        html[data-theme="dark"] .upload-option-btn:hover,
        html[data-theme="dark"] .upload-option-btn:active {
          border-color: #fa4848;
          color: #fa4848;
          background: #2a1a1a;
        }

        html[data-theme="dark"] .file-info {
          background: #2a2a2a;
        }

        html[data-theme="dark"] .file-name {
          color: #e0e0e0;
        }

        html[data-theme="dark"] .file-size {
          color: #888;
        }

        html[data-theme="dark"] .upload-progress {
          background: #2a2a2a;
        }

        html[data-theme="dark"] .progress-text {
          color: #aaa;
        }

        html[data-theme="dark"] .progress-bar {
          background: #3a3a3a;
        }

        html[data-theme="dark"] .modal-actions {
          border-top-color: #333;
        }

        html[data-theme="dark"] .size-warning {
          background: #2a2510;
          border-color: #7a6010;
          color: #d4a017;
        }
      `}</style>

    </div>
  );
};

export default ChatInsertModal;